import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { VolunteerCallSetBody } from '../types'

// Events Live volunteer loop (mig 053): the manager Availability inbox, the
// per-volunteer confirm-all, and the call-for-volunteers composer. Reviewing or
// confirming changes both the offers list and the roster grid, so those
// mutations invalidate both query keys.

export function useVolunteerOffers(eventId: number) {
  return useQuery({
    queryKey: ['volunteer-offers', eventId],
    queryFn: () => ApiClient.volunteerOffers(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useReviewVolunteerOffer(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ offerId, decision }: { offerId: number; decision: 'approved' | 'declined' }) =>
      ApiClient.reviewVolunteerOffer(eventId, offerId, decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['volunteer-offers', eventId] })
    }
  })
}

export function useConfirmVolunteer(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => ApiClient.confirmVolunteer(eventId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['volunteer-offers', eventId] })
      qc.invalidateQueries({ queryKey: ['roster', eventId] })
    }
  })
}

// Group role approval (item 3) — confirm a whole role's roster at once.
export function useConfirmRole(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (roleId: number) => ApiClient.confirmRoleRoster(eventId, roleId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['volunteer-offers', eventId] })
      qc.invalidateQueries({ queryKey: ['roster', eventId] })
    }
  })
}

export function useVolunteerCall(eventId: number) {
  return useQuery({
    queryKey: ['volunteer-call', eventId],
    queryFn: () => ApiClient.volunteerCall(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

export function useSetVolunteerCall(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: VolunteerCallSetBody) => ApiClient.setVolunteerCall(eventId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['volunteer-call', eventId] })
  })
}

export function useCloseVolunteerCall(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ApiClient.closeVolunteerCall(eventId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['volunteer-call', eventId] })
  })
}
